/* global MODULE_NAME UI_VERSION UI_THEME */
/**
 * This is the entrypoint for the Documents module.
 */
import {
  $, appBus, glu, http, templateHelpers
} from '@glu/core';
import React, { StrictMode } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import { Router } from 'react-router-dom';
import { legacyTheme, ashTheme, ThemeProvider } from '@glu/theming';
import { UserProvider, setUserData, getUserData } from 'system/contexts/user';
import { CoreInterfaceProvider, setCoreInterface, getCoreInterface } from 'system/contexts/coreInterface';
import routes from 'system/routes';
import history from 'system/history';
import urls from 'system/urls';
import Gateway from 'system/gateway';
import dialog from '@glu/dialog';
import runApplication from 'system/runApplication';
import locale from 'system/locale';
import fetchLocale, { Brands } from 'system/i18n';
import currentUser from 'system/currentUser';
import localStorage from 'system/localStorage';
import { legacy, ash } from 'system/contexts/theme';
import I18nContext from 'system/contexts/I18n';
import ThemeContext from 'system/contexts/themeContext';
import FeaturesContext from 'system/contexts/featuresContext';
import deepMerge from 'deepmerge';
import { defaultStrings_en_US as gridDefaultStrings } from '@glu/grid-react';
import { defaultStrings_en_US as dataComponentsDefaultStrings } from '@glu/data-components';
import { defaultStrings as validationDefaultStrings, validators } from '@glu/validation-react';

export function getLocale(brandName) {
  templateHelpers.defaultDateFormat = 'L';
  locale.loadBrowserLocale();

  return (async () => {
    const fetchedLocale = await fetchLocale();
    const defaultSettings = ({
      ...gridDefaultStrings,
      ...validationDefaultStrings,
      ...dataComponentsDefaultStrings,
      ...fetchedLocale({
        brand: Brands[brandName].brand,
        brandPtx: Brands[brandName].brandPtx,
        ptxLink: Brands[brandName].ptxLink,
        ptx: Brands[brandName].ptx
      }),
      noRowsToShow: 'No data',
      validator: {
        required: 'Required'
      }
    });
    locale.set(defaultSettings);
    validators.messages = validators.initMessages();

    return Promise.resolve(defaultSettings);
  })();
}

export default Application => runApplication((configFromCore) => {
  window.about = {
    ...window.about,
    [MODULE_NAME]: {
      uiVersion: UI_VERSION,
      gluVersion: glu.VERSION,
      theme: UI_THEME
    }
  };
  /**
   * rootHtmlElementSelector is the 'slot' passed in by Core-UI for
   * IP to render itself in to
   */
  const rootElement = document.querySelector(configFromCore.rootHtmlElementSelector);
  /**
   * Root is the base root passed in from Core-UI. It dictates the start of the URL
   * for the module (eg. /ui/account-visibility)
   */
  const root = configFromCore.root.substring(0, configFromCore.root.length - 1);

  const combinedAshTheme = deepMerge(
    configFromCore.brandTheme, ash(configFromCore.brandTheme)
  );

  const initialisedHistory = history(root);
  const isGluAsh = UI_THEME === 'glu-ash';
  dialog.setBaseTheme(isGluAsh ? ashTheme : legacyTheme);
  const theme = isGluAsh ? combinedAshTheme : legacy;
  let features = {};
  dialog.setTheme(theme);
  return {
    applicationIsAboutToStart({ skipUserFetch }) {
      if (skipUserFetch) {
        return getLocale(configFromCore?.brandName || 'bottomline');
      }
      return new Promise(((resolve) => {
        currentUser.fetch({
          success: () => {
            setUserData({
              ...getUserData(),
              ...currentUser.toJSON()
            });
            // feature flags for Connect
            new Gateway(urls.feature).get()
              .then((response) => {
                features = response;
                resolve();
              });
          },
          error: resolve
        });
      })).then(getLocale(configFromCore?.brandName || 'bottomline'));
    },
    getModuleName: () => 'documents',
    showLayout: () => {
      getLocale(configFromCore?.brandName || 'bottomline').then(loadedLocale => {
        render(
          (
            <StrictMode>
              <Router history={initialisedHistory}>
                <ThemeContext.Provider value={theme}>
                  <CoreInterfaceProvider coreInterface={getCoreInterface()}>
                    <FeaturesContext.Provider value={features}>
                      <I18nContext.Provider value={loadedLocale}>
                        <UserProvider currentUser={getUserData()}>
                          <ThemeProvider
                            baseTheme={isGluAsh ? ashTheme : legacyTheme}
                            theme={isGluAsh ? combinedAshTheme : legacy}
                          >
                            <Application />
                          </ThemeProvider>
                        </UserProvider>
                      </I18nContext.Provider>
                    </FeaturesContext.Provider>
                  </CoreInterfaceProvider>
                </ThemeContext.Provider>
              </Router>
            </StrictMode>
          ),
          rootElement
        );
      });
    },
    unmount: () => unmountComponentAtNode(rootElement),
    authenticate: config => {
      sessionStorage.setItem('httpHeaders', JSON.stringify(config.httpHeaders));
      const {
        httpHeaders = {},
        params = {
          currentUser: {},
          interface: {}
        }
      } = config;
      return new Promise((resolve) => {
        // here we need to set our httpHeaders from config
        sessionStorage.setItem('httpHeaders', JSON.stringify(httpHeaders));
        http.setRequestHeaders({
          ...httpHeaders,
          'Access-Control-Allow-Origin': window.location.origin,
          'Access-Control-Allow-Credentials': 'true',
          'Access-Control-Allow-Methods': 'GET,HEAD,OPTIONS,POST,PUT,DELETE,PATCH',
          'Access-Control-Allow-Headers': 'Access-Control-Allow-Credentials, Access-Control-Allow-Origin, Origin, X-Requested-With, Content-Type, Accept, Authorization, Content-Disposition'
        });
        setCoreInterface(config.interface);
        setUserData({
          customerId: params.companyId,
          ...params.currentUser,
          ...currentUser.toJSON()
        });
        currentUser.set({
          currentUser: {
            ...params.currentUser
          },
          customerId: params.companyId,
          httpHeaders
        });
        currentUser.coreInterface = config.interface;
        localStorage.clearExpiredStorage();
        appBus.trigger('update:app', { ...config });
        $.ajaxSetup({
          xhrFields: {
            withCredentials: false
          },
          crossDomain: true
        });
        resolve(config);
      });
    },
    getRoutes: () => routes.routes,
    getRouter: () => {},
    applicationIsAboutToStop: () => Promise.resolve(),
    loadUrl: () => history().loadUrl(`${window.location.pathname}${window.location.search}`),
    stop: () => history().unlisten()
  };
});